// SF1 — what is promoting right now.
//
// THE STATE IS AMAZON'S, NEVER A DATE COMPARISON. Every row here is live
// because the Promotions API answered RUNNING, not because today falls between
// a start and an end. A coupon stops the moment its budget is spent, which is
// usually well before its end date, so a date range would show stopped coupons
// as running — and the reported calendar beside this one would still disagree,
// because a report lags.
//
// WHAT THIS SURFACE IS FOR is the window where a promotion can still be
// changed. So the soonest to end sorts first, and budget burn is called out on
// the row: those are the two things that decide whether a seller acts today.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Promotions.module.css';
import { getLivePromotions } from '../services/promotionsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

// "in 9 hours" reads as something to act on; a timestamp does not.
const When = ({ hours, past }) => {
  if (hours === null || hours === undefined) return <span className={local.blank}>—</span>;
  if (hours < 0) return <span className={local.context}>{past || 'already'}</span>;
  if (hours < 1) return <span className={local.lost}>within the hour</span>;
  if (hours < 24) {
    const whole = Math.round(hours);
    return <span className={local.lost}>in {whole} hour{whole === 1 ? '' : 's'}</span>;
  }
  const days = Math.round(hours / 24);
  return <span className={local.context}>in {days} day{days === 1 ? '' : 's'}</span>;
};

// Budget is the one figure here that comes from a report rather than live, so
// it is shown with that stated rather than blended in with the live columns.
const Budget = ({ budget, nearlySpent }) => {
  if (!budget) return <span className={local.blank} title="No coupon report row matched this promotion">—</span>;
  const used = budget.fraction_used;
  if (used === null || used === undefined) return <span className={local.blank}>—</span>;
  const pct = Math.round(used * 100);
  return (
    <span className={nearlySpent ? local.lost : local.context}>
      {pct}% spent
      {budget.remaining !== null && budget.remaining !== undefined
        && <span className={local.context}> · ₹{inr.format(Math.round(budget.remaining))} left</span>}
    </span>
  );
};

const RunningNow = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (!token) return;
    setLoading(true); setError(null);
    try { setData(await getLivePromotions(token, { refresh })); }
    catch (err) { setError(err); setData(null); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const running = data?.running || [];
  const upcoming = data?.upcoming || [];
  const counts = data?.counts;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What is running right now</h2>
        <div className={local.controls}>
          {counts && (
            <span className={local.muted}>
              {counts.running} running
              {counts.ending_within_24h > 0 && ` · ${counts.ending_within_24h} ending within 24h`}
              {counts.budget_nearly_spent > 0 && ` · ${counts.budget_nearly_spent} nearly out of budget`}
            </span>
          )}
          <button type="button" className={local.segment} onClick={() => load(true)} disabled={loading}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      <p className={local.muted}>
        Live state from the Promotions API, not from a report. A coupon stops
        when its budget is spent, so a promotion inside its dates is not
        necessarily still running — this asks Amazon rather than working it out
        from the calendar.
      </p>

      {error && (
        <div className={`${local.banner} ${local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : 'Could not read live promotion state'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th>Promotion</th>
                <th>Type</th>
                <th>Ends</th>
                <th>Budget</th>
              </tr>
            </thead>
            <tbody>
              {running.length === 0 ? (
                <tr><td className={local.emptyCell} colSpan={4}>
                  {loading ? 'Loading…'
                    : 'Amazon reports nothing running. This is the live state, not missing data.'}
                </td></tr>
              ) : running.map((row) => (
                <tr key={row.id}>
                  <td>{row.title || row.id}</td>
                  <td className={local.context}>{(row.type || '').replace(/_/g, ' ').toLowerCase()}</td>
                  <td><When hours={row.hours_until_end} past="ending now" /></td>
                  <td><Budget budget={row.budget} nearlySpent={row.budget_nearly_spent} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!error && upcoming.length > 0 && (
        <>
          <h3 className={local.indexGroupTitle}>Starting next</h3>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr><th>Promotion</th><th>Type</th><th>Starts</th></tr>
              </thead>
              <tbody>
                {upcoming.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title || row.id}</td>
                    <td className={local.context}>{(row.type || '').replace(/_/g, ' ').toLowerCase()}</td>
                    <td><When hours={row.hours_until_start} past="starting now" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data?.as_of && (
        <p className={local.coverage}>
          Read from Amazon at {new Date(data.as_of + 'Z').toLocaleTimeString()}.
          Cached for five minutes — Refresh to read again.
        </p>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      )}
    </section>
  );
};

export default RunningNow;
